import React from "react";
import { Formik, Form } from "formik";
import { Colors } from "@jsq/ds/styles";
import { HelpIndicator, Text, PhoneInput, type CountryCodeData } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { SetupCard } from "../components/SetupCard";
import { type UnauthenticatedArenaObjectType } from "../../../types";

type TwoFaNumberFormikProps = {
  phoneNumber: string;
  countryCode: string;
  dialCode: string;
};

type Props = {
  onSubmit: (phoneNumber: string, countryCode: string) => void;
  title: string;
  subtitle: string;
  submitButtonText: string;
  onSwitch2FAMethodButtonClick?: () => void;
  showHeader?: boolean;
  arena?: UnauthenticatedArenaObjectType;
  isSplitScreen?: boolean;
};

/**
 * A component for setting up 2FA with an SMS phone number.
 * Gets a phone number and country code from user input and sets these values as state in the parent component
 * using passed in state setters. Sending the code is handled in the verification step.
 */
export const SMS2FASetupVariant = (props: Props) => {
  const [isValidPhoneNumber, setIsValidPhoneNumber] = React.useState(false);
  const handleSubmit = (values: TwoFaNumberFormikProps) => {
    const phoneNumber = values.phoneNumber.slice(values.dialCode.length);
    const countryCode = values.countryCode.toUpperCase();

    props.onSubmit(phoneNumber, countryCode);
  };

  const handleSetValue = (
    phoneNumber: string,
    data: CountryCodeData,
    setFieldValue: (field: string, value: string, shouldValidate?: boolean) => void,
  ) => {
    setFieldValue("phoneNumber", phoneNumber);
    setFieldValue("countryCode", data.countryCode);
    setFieldValue("dialCode", data.dialCode);
  };

  const footer = !props.onSwitch2FAMethodButtonClick ? (
    <Text align="center" variant="secondary-sm" data-cy="account-two-factor-auth-text">
      This account requires text message two-factor authentication&nbsp;
      <HelpIndicator
        placement="top"
        content="You have both manager and investor accounts. For security reasons, you can only use text message as your two-factor authentication method."
      >
        <Icon color={Colors.Neutral600} type="question-circle" size="small" variant="outline" />
      </HelpIndicator>
    </Text>
  ) : undefined;

  // We are using the presence of the onSwitch2FAMethodButtonClick callback to determine if this is rendering for an LP user
  // because GP/Hybrid users do not have the ability to switch 2FA methods to email and therefore would not have this callback
  const isLPUser = Boolean(props.onSwitch2FAMethodButtonClick);

  return (
    <Formik
      initialValues={{ phoneNumber: "", countryCode: "us", dialCode: "1" }}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, isSubmitting, submitForm, values }) => (
        <SetupCard
          onSwitch2FAMethodButtonClick={props.onSwitch2FAMethodButtonClick}
          switch2FAMethodButtonText="Try another method to authenticate"
          methodTitle={props.title}
          methodSubtitle={props.subtitle}
          submitButtonText={props.submitButtonText}
          isSubmitting={isSubmitting}
          onSubmit={submitForm}
          isSubmitButtonDisabled={!isValidPhoneNumber}
          showHeader={props.showHeader}
          // @ts-expect-error code written before better codegen types
          submitButtonBackgroundColor={props.arena?.signInButtonBackgroundColor}
          // @ts-expect-error code written before better codegen types
          submitButtonTextColor={props.arena?.signInButtonTextColor}
          isSplitScreen={props.isSplitScreen}
          footer={footer}
        >
          <Form
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit(values);
              }
            }}
          >
            <PhoneInput
              data-cy="your-phone-number-text"
              label="Your mobile phone number"
              isFullWidth
              onSetValue={(phoneNumber, data) =>
                handleSetValue(phoneNumber, data, setFieldValue)
              }
              value=""
              helpText="You will receive a text message with a 6-digit code to sign in"
              name="phoneNumber"
              onIsValidChange={setIsValidPhoneNumber}
              isLPUser={isLPUser}
            />
          </Form>
        </SetupCard>
      )}
    </Formik>
  );
};

SMS2FASetupVariant.defaultProps = {
  showHeader: true,
};

export default SMS2FASetupVariant;
